import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Modal, Flex, List, Button, Input, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import CustomAvatar from "../../../Components/CustomAvatar";
import ApiService from "../../../API/apiService";
import { API_ENDPOINTS } from "../../../API/endpoints";
import { handleApiError } from "../../../utils/CommonFunctions";

const DirectMessageModal = ({ getChatListApi, userSelfData, onModalUpdate }) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const [searchText, setSearchText] = useState("");
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [totalSearchUsers, setTotalSearchUsers] = useState(null);
  const [searchCounts, setSearchCounts] = useState({});
  const [userName, setUserName] = useState([]);
  const [hoverState, setHoverState] = useState("");
  const [showLoader, setShowLoader] = useState(false);

  const userSuggestions = useCallback(async () => {
    try {
      const response = await ApiService.get(API_ENDPOINTS.userSuggestions);
      if (response?.data) {
        setSuggestedUsers(response.data.suggestions || []);
        setTotalSearchUsers(response.data.total_users);
      }
    } catch (error) {
      handleApiError(error);
    }
  }, []);

  const searchUserApi = useCallback(async (searchTerm) => {
    if (!searchTerm.trim()) return;
    
    setShowLoader(true);
    try {
      const response = await ApiService.post(API_ENDPOINTS.getSearchUserList, {
        searchTerm,
      });
      
      if (response?.data && response?.status === 1) {
        setSearchCounts(response.data);
        setUserName(response.data.users || []);
      } else {
        setUserName([]);
        setSearchCounts({});
      }
    } catch (error) {
      handleApiError(error);
      setUserName([]);
      setSearchCounts({});
    } finally {
      setShowLoader(false);
    }
  }, []);

  const debouncedSearchUserApi = useMemo(
    () => debounce(searchUserApi, 400),
    [searchUserApi]
  );

  useEffect(() => {
    userSuggestions();
    inputRef.current?.focus();
  }, [userSuggestions]);

  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    setSearchText(value);

    if (value) {
      debouncedSearchUserApi(value);
    } else {
      setUserName([]);
      setSearchCounts({});
      userSuggestions();
    }
  }, [debouncedSearchUserApi, userSuggestions]);

  const closeModal = useCallback(() => {
    setSearchText("");
    onModalUpdate({ activeModal: "" });
  }, [onModalUpdate]);

  const addUserInChatApi = useCallback(async (id) => {
    if (!id || !userSelfData?._id) {
      console.error("Missing required parameters");
      return;
    }

    try {
      await ApiService.post(API_ENDPOINTS.addUserInChatList, {
        userId: userSelfData._id,
        selectedUserId: id,
      });
    } catch (error) {
      // ignore — user may already be in chat list
    }

    localStorage.setItem("current_id", id);
    navigate(`/elsnertech/message/${id}`);
    setUserName([]);
    closeModal();
    getChatListApi();
  }, [userSelfData?._id, navigate, getChatListApi, closeModal]);

  const filteredUsers = useMemo(() => {
    if (!searchText) return suggestedUsers;
    
    const lowerSearchText = searchText.toLowerCase();
    return userName.filter(
      (user) =>
        user.username?.toLowerCase().includes(lowerSearchText) ||
        user.fullName?.toLowerCase().includes(lowerSearchText)
    );
  }, [searchText, userName, suggestedUsers]);

  const memberCountText = useMemo(() => {
    if (searchText) {
      return `${searchCounts.totalSearchResults || 0} of ${searchCounts.total_users || 0} members`;
    }
    return `${suggestedUsers.length || 0} of ${totalSearchUsers || 0} members`;
  }, [searchText, searchCounts, suggestedUsers.length, totalSearchUsers]);

  const handleUserClick = useCallback(async (userId) => {
    await addUserInChatApi(userId);
    closeModal();
  }, [addUserInChatApi, closeModal]);

  const renderUserItem = useCallback((item, isSearchResult = false) => {
    const userId = isSearchResult ? item._id : item.userId;
    
    return (
      <List.Item
        key={userId}
        onMouseEnter={() => setHoverState(userId)}
        className={hoverState === userId ? "hoverClass" : ""}
        onClick={() => handleUserClick(userId)}
        style={{ cursor: "pointer" }}
      >
        <List.Item.Meta
          avatar={
            <CustomAvatar
              img={item?.thumbnail_avatarUrl}
              size={40}
              userName={item?.fullName || item?.username}
            />
          }
          title={
            <>
              <span className="title">{item?.username || ""}</span>
              {item?.fullName && <span className="subtitle"> - {item.fullName}</span>}
              {item?.position && <span className="designation"> - {item.position}</span>}
            </>
          }
          description={item.elsner_email || item.email}
        />
        <Button type="text" className="add-btn">
          {item.content}
        </Button>
      </List.Item>
    );
  }, [hoverState, handleUserClick]);

  return (
    <Modal
      title="Direct Messages"
      open={true}
      onCancel={closeModal}
      footer={null}
      className="ec-modal direct-message-search-modal"
      width={600}
      style={{ top: 20 }}
      destroyOnHidden
    >
      <Flex gap="small" className="ant-modal-subheader">
        <div className="search-container-wrapper">
          <div className="search-container">
            <div className="search-tags-wrapper">
              <Input
                className="search-input"
                placeholder="Search names"
                value={searchText}
                onChange={handleInputChange}
                ref={inputRef}
              />
            </div>
          </div>
        </div>
      </Flex>

      <Flex justify="space-between" gap="small" className="member-details">
        <p>{memberCountText}</p>
      </Flex>

      <Flex vertical className="suggestion-list-container">
        {showLoader ? (
          <Spin size="default" />
        ) : (
          <>
            {searchText ? (
              filteredUsers.length > 0 ? (
                <List
                  className="suggestion-list"
                  itemLayout="horizontal"
                  dataSource={filteredUsers}
                  renderItem={(item) => renderUserItem(item, true)}
                />
              ) : (
                <p className="no-results-message">
                  No results found matching "{searchText}"
                </p>
              )
            ) : (
              suggestedUsers.length > 0 && (
                <List
                  className="suggestion-list"
                  itemLayout="horizontal"
                  dataSource={suggestedUsers}
                  renderItem={(item) => renderUserItem(item, false)}
                />
              )
            )}
          </>
        )}
      </Flex>
    </Modal>
  );
};

export default DirectMessageModal;